import { canTenantRole, type TenantRole } from "@loot-os/api-contract";
import type { LucideIcon } from "lucide-react";

/**
 * The pure side of the ⌘K palette: what the caller may navigate to, and which of those rows a
 * query keeps. Takes structural views of the two registries and the tenant list rather than
 * the modules themselves, so a test builds an index from a few literals and no icons.
 * Server-side results (shoppers, boxes) never pass through here; the palette renders them
 * straight from the query.
 */

/** A platform tool as the index reads it: `PlatformTool` is assignable. */
export interface CommandToolSource {
  label: string;
  description: string;
  to: string;
  icon?: LucideIcon;
  /** Extra terms staff call the tool by; a prefix match on one outranks a haystack hit. */
  keywords?: readonly string[];
}

/** A hub's section page as the index reads it: a `TenantNavChild` is assignable. */
export interface CommandNavChildSource {
  /** The route with its `$tenantId` param still in it. */
  to: string;
  label: string;
  /** Extra terms staff call the page by, beside its route segment. */
  keywords?: readonly string[];
  /** A section page's own role when it serves a higher one than its hub. */
  minRole?: TenantRole;
}

/** One tenant page as the index reads it: a `TENANT_NAV` item is assignable. */
export interface CommandNavItemSource extends CommandNavChildSource {
  icon?: LucideIcon;
  minRole: TenantRole;
  /** The hub's section pages; they inherit `minRole` unless they set one. */
  children?: readonly CommandNavChildSource[];
}

export interface CommandNavGroupSource {
  /** The sidebar's section label; null for the unlabelled top section. */
  label: string | null;
  items: readonly CommandNavItemSource[];
}

/** A tenant as `me.tenants` returns it. */
export interface CommandTenantSource {
  id: string;
  name: string;
  slug: string;
  archivedAt: Date | string | null;
}

export type CommandGroupName = "Navigation" | "Tenants";

/** One row the palette can navigate to. */
export interface CommandTarget {
  /** Unique across the index: cmdk selects by value, so two rows must never share one. */
  id: string;
  label: string;
  group: CommandGroupName;
  /** An absolute path inside the app, handed to the router as `href`. */
  href: string;
  description?: string;
  icon?: LucideIcon;
  /** Lowercased extra terms. */
  keywords: readonly string[];
  /** Precomputed lowercase text every token of a query must hit. */
  haystack: string;
}

export interface BuildCommandIndexInput {
  platformTools: readonly CommandToolSource[];
  tenantNav: readonly CommandNavGroupSource[];
  tenants: readonly CommandTenantSource[];
  /** The route's tenant, or null outside one. */
  tenantId: string | null;
  isPlatformAdmin: boolean;
  /** The caller's role in `tenantId`; null while it loads or outside a tenant. */
  role: string | null;
}

function makeTarget(
  target: Omit<CommandTarget, "keywords" | "haystack"> & { keywords?: readonly string[] },
): CommandTarget {
  const keywords = (target.keywords ?? []).map((keyword) => keyword.toLowerCase());
  const haystack = [target.label, target.description ?? "", ...keywords, target.group]
    .join(" ")
    .toLowerCase();
  return { ...target, keywords, haystack };
}

/** The route's last segment (`users`, `domains`), a synonym staff type; none for a param. */
function routeSegmentKeyword(to: string): string[] {
  const segment = to.split("/").at(-1);
  return segment && !segment.startsWith("$") ? [segment] : [];
}

/**
 * Every row the caller may open: the platform tools for a platform admin, the tenant's
 * pages the caller's role reaches while inside a tenant (a hub's section pages right after
 * it), and every tenant the caller belongs to (all of them for a platform admin, archived
 * ones flagged). Registry order. A page in the sidebar's unlabelled section is described by
 * the tenant's name, a section page by its hub.
 */
export function buildCommandIndex(input: BuildCommandIndexInput): CommandTarget[] {
  const targets: CommandTarget[] = [];
  if (input.isPlatformAdmin) {
    for (const tool of input.platformTools) {
      targets.push(
        makeTarget({
          id: `nav:${tool.to}`,
          label: tool.label,
          group: "Navigation",
          href: tool.to,
          description: tool.description,
          icon: tool.icon,
          keywords: tool.keywords,
        }),
      );
    }
  }
  const { tenantId, role } = input;
  if (tenantId && role) {
    const tenantName = input.tenants.find((tenant) => tenant.id === tenantId)?.name;
    const pushPage = (
      page: CommandNavChildSource,
      description: string | undefined,
      icon?: LucideIcon,
    ) => {
      const href = page.to.replace("$tenantId", tenantId);
      targets.push(
        makeTarget({
          id: `nav:${href}`,
          label: page.label,
          group: "Navigation",
          href,
          description,
          icon,
          keywords: [...routeSegmentKeyword(page.to), ...(page.keywords ?? [])],
        }),
      );
    };
    for (const group of input.tenantNav) {
      for (const item of group.items) {
        if (!canTenantRole(role, item.minRole)) continue;
        pushPage(item, group.label ?? tenantName, item.icon);
        for (const child of item.children ?? []) {
          if (!canTenantRole(role, child.minRole ?? item.minRole)) continue;
          pushPage(child, item.label, item.icon);
        }
      }
    }
  }
  for (const tenant of input.tenants) {
    targets.push(
      makeTarget({
        id: `tenant:${tenant.id}`,
        label: tenant.name,
        group: "Tenants",
        href: `/tenants/${tenant.id}`,
        description: tenant.archivedAt ? `${tenant.slug} · archived` : tenant.slug,
        keywords: [tenant.slug, tenant.id],
      }),
    );
  }
  return targets;
}

function scoreTarget(target: CommandTarget, query: string): number {
  const label = target.label.toLowerCase();
  if (label.startsWith(query)) return 5;
  if (label.split(/\s+/).some((word) => word.startsWith(query))) return 4;
  if (label.includes(query)) return 3;
  if (target.keywords.some((keyword) => keyword.startsWith(query))) return 2;
  return 1;
}

/**
 * An empty query lists the navigation rows in registry order, plus the tenants unless
 * `hideTenantsOnEmpty` (a platform admin's full tenant list is noise until typed for; a
 * member's few tenants are the useful default). Otherwise every whitespace-separated token
 * must substring-match the haystack, ranked label-prefix > label-word-prefix >
 * label-substring > keyword-prefix > haystack, index order as the tiebreak.
 */
export function filterCommands(
  index: readonly CommandTarget[],
  query: string,
  opts: { hideTenantsOnEmpty: boolean },
): CommandTarget[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return index.filter((target) => !(opts.hideTenantsOnEmpty && target.group === "Tenants"));
  }
  const tokens = q.split(/\s+/);
  const scored: { target: CommandTarget; score: number; order: number }[] = [];
  for (const [order, target] of index.entries()) {
    if (!tokens.every((token) => target.haystack.includes(token))) continue;
    scored.push({ target, score: scoreTarget(target, q), order });
  }
  scored.sort((x, y) => y.score - x.score || x.order - y.order);
  return scored.map((entry) => entry.target);
}

export interface CommandGroupRows {
  group: CommandGroupName;
  items: CommandTarget[];
}

const GROUP_ORDER: readonly CommandGroupName[] = ["Navigation", "Tenants"];

/** The rows by group in the palette's fixed order, each group's rows in the order given; empty groups dropped. */
export function groupCommands(targets: readonly CommandTarget[]): CommandGroupRows[] {
  return GROUP_ORDER.map((group) => ({
    group,
    items: targets.filter((target) => target.group === group),
  })).filter((group) => group.items.length > 0);
}
